import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { AlertCircle, Loader2, CheckCircle, Clock, BarChart3 } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

interface WhatsAppLog {
  id: string;
  message: string;
  direction: 'inbound' | 'outbound';
  status: string;
  created_at: string;
  customer: {
    name: string;
    phone: string;
  } | null;
}

interface Stats {
  total_sent: number;
  total_delivered: number;
  total_failed: number;
  delivery_rate: number;
}

export default function WhatsAppSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [businessId, setBusinessId] = useState<string>('');
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);

  const [logs, setLogs] = useState<WhatsAppLog[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [activeTab, setActiveTab] = useState<'history' | 'stats'>('history');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data: userData, error: userErr } = await supabase
        .from('users')
        .select('business_id')
        .eq('id', user.id)
        .single();

      if (userErr) throw userErr;
      if (!userData?.business_id) {
        setLoading(false);
        return;
      }

      const bId = userData.business_id as string;
      setBusinessId(bId);

      const [businessRes, logsRes, statsRes] = await Promise.all([
        supabase
          .from('businesses')
          .select('whatsapp_enabled')
          .eq('id', bId)
          .single(),

        supabase
          .from('whatsapp_logs')
          .select(`
            id,
            message,
            direction,
            status,
            created_at,
            customer:customers(name, phone)
          `)
          .eq('business_id', bId)
          .order('created_at', { ascending: false })
          .limit(20),

        supabase
          .from('whatsapp_logs')
          .select('status')
          .eq('business_id', bId)
          .eq('direction', 'outbound'),
      ]);

      if (businessRes.error) throw businessRes.error;
      setWhatsappEnabled(!!businessRes.data?.whatsapp_enabled);

      if (logsRes.error) throw logsRes.error;
      setLogs((logsRes.data || []) as WhatsAppLog[]);

      if (statsRes.error) throw statsRes.error;
      const total = statsRes.data?.length || 0;
      const delivered = (statsRes.data || []).filter(l => l.status === 'delivered' || l.status === 'read').length;
      const failed = (statsRes.data || []).filter(l => l.status === 'failed').length;

      setStats({
        total_sent: total,
        total_delivered: delivered,
        total_failed: failed,
        delivery_rate: total > 0 ? Math.round((delivered / total) * 100) : 0,
      });

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('שגיאה בטעינת נתונים');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWhatsApp = async () => {
    if (!businessId) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({ whatsapp_enabled: !whatsappEnabled })
        .eq('id', businessId);

      if (error) throw error;

      setWhatsappEnabled(!whatsappEnabled);
      toast.success(!whatsappEnabled ? 'WhatsApp הופעל בהצלחה' : 'WhatsApp כובה בהצלחה');
    } catch (error) {
      console.error('Error toggling WhatsApp:', error);
      toast.error('שגיאה בעדכון הגדרות');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">הגדרות WhatsApp</h1>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-blue-800 font-medium mb-1">
              מערכת WhatsApp מרכזית של LoyalFlow
            </p>
            <p className="text-sm text-blue-700">
              כל ההודעות נשלחות אוטומטית ממספר WhatsApp מרכזי של LoyalFlow.
              פשוט הפעל את השירות וההודעות יישלחו אוטומטית ללקוחות שלך.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold">סטטוס שירות</h2>
            <p className="text-sm text-gray-600 mt-1">הפעל או כבה את שליחת ההודעות האוטומטיות</p>
          </div>
          <div className="flex items-center gap-3">
            {whatsappEnabled ? (
              <span className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                פעיל
              </span>
            ) : (
              <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                כבוי
              </span>
            )}
          </div>
        </div>

        <button
          onClick={handleToggleWhatsApp}
          disabled={saving}
          className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
            whatsappEnabled
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-green-600 hover:bg-green-700 text-white'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {whatsappEnabled ? 'כבה WhatsApp' : 'הפעל WhatsApp'}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 min-w-[120px] px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'history'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Clock className="w-4 h-4 inline-block ml-2" />
              היסטוריה
            </button>

            <button
              onClick={() => setActiveTab('stats')}
              className={`flex-1 min-w-[120px] px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'stats'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline-block ml-2" />
              סטטיסטיקות
            </button>
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">היסטוריית הודעות</h3>

              {logs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">אין הודעות עדיין</p>
              ) : (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div key={log.id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {log.customer?.name || 'לקוח לא ידוע'}
                          </p>
                          <p className="text-sm text-gray-600">{log.customer?.phone || ''}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              log.status === 'delivered' || log.status === 'read'
                                ? 'bg-green-100 text-green-800'
                                : log.status === 'failed'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {log.status === 'delivered' ? 'נמסר' :
                             log.status === 'read' ? 'נקרא' :
                             log.status === 'sent' ? 'נשלח' :
                             log.status === 'failed' ? 'נכשל' : log.status}
                          </span>
                          <span className="text-xs text-gray-500">
                            {format(new Date(log.created_at), 'dd/MM/yy HH:mm')}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 line-clamp-2">{log.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'stats' && stats && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold mb-4">סטטיסטיקות</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-700 mb-1">סה״כ נשלחו</p>
                  <p className="text-3xl font-bold text-blue-900">{stats.total_sent}</p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-700 mb-1">נמסרו בהצלחה</p>
                  <p className="text-3xl font-bold text-green-900">{stats.total_delivered}</p>
                </div>

                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-red-700 mb-1">נכשלו</p>
                  <p className="text-3xl font-bold text-red-900">{stats.total_failed}</p>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-purple-700 mb-1">אחוז הצלחה</p>
                  <p className="text-3xl font-bold text-purple-900">{stats.delivery_rate}%</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
